import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Alert, Badge, Spinner, Button, Accordion } from 'react-bootstrap';
import { formatCurrency } from '../../utils/formatters';
import { formatTimestamp } from '../../utils/dateFormatters';
import { fetchCheckImages, updateCheckImageData, deleteCheckImage } from '../../api/cases';
import { useAuth } from '../../contexts/AuthContext';
import { PencilSquare } from 'react-bootstrap-icons';
import { CheckImage } from '../../types';
import CheckImageEditor from './CheckImageEditor';


interface CheckImagesViewProps {
  caseId: string;
  fileId: string;
  onGoToPage?: (page: number) => void;
}

const CheckImagesView: React.FC<CheckImagesViewProps> = ({ caseId, fileId, onGoToPage }) => {
  const [checkImages, setCheckImages] = useState<CheckImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingImageId, setEditingImageId] = useState<string | null>(null);
  const [savingChanges, setSavingChanges] = useState(false);
  const { getAccessToken } = useAuth();

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        const token = await getAccessToken();
        const response = await fetchCheckImages(caseId, fileId, token);
        setCheckImages(response.data.check_images || []);
      } catch (err: any) {
        console.error('Error loading check images:', err);
        setError('Failed to load check images');
      } finally {
        setLoading(false);
      }
    };

    if (caseId && fileId) {
      loadData();
    }
  }, [caseId, fileId]);

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 80) return 'success';
    if (confidence >= 60) return 'warning';
    return 'danger';
  };

  const formatCheckAmount = (amount: string | undefined) => {
    if (!amount || amount.trim() === '') return 'Not detected';
    
    // GPT-4V usually returns amounts in a good format already
    // If it starts with $, return as-is, otherwise add $ if it looks like a number
    const cleanAmount = amount.trim();
    if (cleanAmount.startsWith('$')) {
      return cleanAmount;
    }
    
    // Check if it looks like a number (with or without commas/decimals)
    const match = cleanAmount.match(/^\d+(?:,\d{3})*(?:\.\d{2})?$/);
    if (match) {
      const num = parseFloat(cleanAmount.replace(/,/g, ''));
      return formatCurrency(num);
    }
    
    // Return original if it doesn't look like a standard number
    return cleanAmount;
  };

  const formatCheckDate = (date: string | undefined) => {
    if (!date || date.trim() === '') return 'Not detected';
    
    // Return the original date string since GPT-4V usually returns well-formatted dates
    // and we don't want to risk changing the format or causing parsing errors
    return date.trim();
  };

  const handleImageClick = (image: CheckImage) => {
    setSelectedImage(image);
    setShowImageModal(true);
  };

  const handleSaveCheckImage = async (updatedCheckImage: CheckImage) => {
    if (!caseId || !fileId || !updatedCheckImage.check_image_id) return;

    try {
      setSavingChanges(true);
      const token = await getAccessToken();
      await updateCheckImageData(caseId, fileId, updatedCheckImage.check_image_id, updatedCheckImage, token);
      setCheckImages(prev =>
        prev.map(img => img.check_image_id === updatedCheckImage.check_image_id ? updatedCheckImage : img)
      );
      setEditingImageId(null);
    } catch (err) {
      console.error('Failed to save check image changes:', err);
      setError('Failed to save changes. Please try again.');
    } finally {
      setSavingChanges(false);
    }
  };

  const handleDeleteCheckImage = async (checkImageId: string) => {
    if (!caseId || !fileId) return;

    try {
      setSavingChanges(true);
      const token = await getAccessToken();
      await deleteCheckImage(caseId, fileId, checkImageId, token);
      setCheckImages(prev => prev.filter(img => img.check_image_id !== checkImageId));
      setEditingImageId(null);
    } catch (err) {
      console.error('Failed to delete check image:', err);
      setError('Failed to delete check image. Please try again.');
    } finally {
      setSavingChanges(false);
    }
  };

  // Group checks by page_number (sorted), then by check_number within each page
  const pageGroups = React.useMemo(() => {
    const sorted = [...checkImages].sort((a, b) => {
      if (a.page_number !== b.page_number) return a.page_number - b.page_number;
      const aNum = parseInt((a.check_number || '').replace(/\D/g, '') || '0', 10);
      const bNum = parseInt((b.check_number || '').replace(/\D/g, '') || '0', 10);
      return aNum - bNum;
    });
    const map = new Map<number, CheckImage[]>();
    for (const img of sorted) {
      if (!map.has(img.page_number)) map.set(img.page_number, []);
      map.get(img.page_number)!.push(img);
    }
    return Array.from(map.entries()).map(([page, checks]) => ({ page, checks }));
  }, [checkImages]);

  // Assign a stable global check index (#1, #2, …) for fallback labelling when check_number is absent
  const checkIndexMap = React.useMemo(() => {
    const ordered = pageGroups.flatMap(g => g.checks);
    return new Map(ordered.map((img, i) => [img.image_id, i + 1]));
  }, [pageGroups]);

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading check images...</span>
        </Spinner>
        <div className="mt-3">Loading extracted check images...</div>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger">
        <Alert.Heading>Error Loading Check Images</Alert.Heading>
        <p>{error}</p>
      </Alert>
    );
  }

  if (checkImages.length === 0) {
    return (
      <Alert variant="info">
        <Alert.Heading>No Check Images Found</Alert.Heading>
        <p>No check images have been extracted from this file yet. The file may still be processing, or it may not contain any check images.</p>
      </Alert>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0">Extracted Check Images ({checkImages.length})</h5>
        <Badge bg="info">
          Completed {formatTimestamp(checkImages[0]?.extracted_date)}
        </Badge>
      </div>

      {savingChanges && (
        <Alert variant="info" className="mb-3">Saving changes...</Alert>
      )}

      <Accordion alwaysOpen>
        {pageGroups.map(({ page, checks }) => (
          <Accordion.Item key={page} eventKey={String(page)}>
            <Accordion.Header>
              <div className="d-flex justify-content-between align-items-center w-100 pe-3">
                <span className="fw-semibold">Page {page}</span>
                <div className="d-flex align-items-center gap-2" onClick={e => e.stopPropagation()}>
                  <Badge bg="secondary" pill>
                    {checks.length} check{checks.length !== 1 ? 's' : ''}
                  </Badge>
                  {onGoToPage && (
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => onGoToPage(page)}
                      style={{ fontSize: '0.75rem', padding: '2px 8px' }}
                    >
                      <i className="bi bi-eye me-1" />Jump to Page
                    </Button>
                  )}
                </div>
              </div>
            </Accordion.Header>

            <Accordion.Body className="p-2">
              <div className="d-flex flex-column gap-2">
                {checks.map(image => {
                  const checkNum = image.check_number || image.check_data?.check_number;
                  const checkTitle = checkNum
                    ? `Check #${checkNum}`
                    : `Check ${checkIndexMap.get(image.image_id)}`;

                  return (
                    <Card key={image.image_id} className="shadow-sm">
                      <Card.Header className="bg-light py-2">
                        <div className="d-flex justify-content-between align-items-center">
                          <h6 className="mb-0">{checkTitle}</h6>
                          <div className="d-flex gap-2 align-items-center">
                            {image.ocr_confidence != null && (
                              <Badge bg={getConfidenceColor(image.ocr_confidence)}>
                                {image.ocr_confidence.toFixed(1)}%
                              </Badge>
                            )}
                            {editingImageId !== image.image_id && (
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => setEditingImageId(image.image_id)}
                                disabled={savingChanges}
                              >
                                <PencilSquare className="me-1" />Edit
                              </Button>
                            )}
                          </div>
                        </div>
                      </Card.Header>

                      <Card.Body className="py-2">
                        {editingImageId === image.image_id ? (
                          <CheckImageEditor
                            checkImage={image}
                            onSave={handleSaveCheckImage}
                            onCancel={() => setEditingImageId(null)}
                            onDelete={() => handleDeleteCheckImage(image.check_image_id)}
                          />
                        ) : (
                          <Row className="g-2">
                            <Col sm={6}>
                              <label className="form-label small text-muted mb-0">Check Number</label>
                              <div className="fw-bold">
                                {image.check_number || image.check_data?.check_number || 'Not detected'}
                              </div>
                            </Col>
                            <Col sm={6}>
                              <label className="form-label small text-muted mb-0">Date</label>
                              <div className="fw-bold">
                                {formatCheckDate(image.date || image.check_data?.date)}
                              </div>
                            </Col>
                            <Col sm={6}>
                              <label className="form-label small text-muted mb-0">Amount</label>
                              <div className={`fw-bold fs-5 ${(image.amount || image.check_data?.amount) ? 'text-success' : 'text-muted'}`}>
                                {formatCheckAmount(image.amount || image.check_data?.amount)}
                              </div>
                            </Col>
                            <Col sm={6}>
                              <label className="form-label small text-muted mb-0">Pay To</label>
                              <div className="fw-bold">
                                {image.payee || image.check_data?.payee || 'Not detected'}
                              </div>
                            </Col>
                            <Col sm={12}>
                              <label className="form-label small text-muted mb-0">Bank Name</label>
                              <div className="fw-bold">{image.bank_name || 'Not detected'}</div>
                            </Col>
                            {image.account_number && (
                              <Col sm={6}>
                                <label className="form-label small text-muted mb-0">Account Number</label>
                                <div className="fw-bold">{image.account_number}</div>
                              </Col>
                            )}
                            {image.routing_number && (
                              <Col sm={6}>
                                <label className="form-label small text-muted mb-0">Routing Number</label>
                                <div className="fw-bold">{image.routing_number}</div>
                              </Col>
                            )}
                            <Col sm={12}>
                              <details className="mt-1">
                                <summary className="small text-muted" style={{ cursor: 'pointer' }}>Raw Extracted Data</summary>
                                <div className="small mt-1 p-2 bg-light rounded" style={{ maxHeight: '160px', overflow: 'auto' }}>
                                  <pre className="mb-0" style={{ fontSize: '0.7rem', whiteSpace: 'pre-wrap' }}>
                                    {JSON.stringify({
                                      check_number: image.check_number,
                                      amount: image.amount,
                                      date: image.date,
                                      payee: image.payee,
                                      bank_name: image.bank_name,
                                      account_number: image.account_number,
                                      routing_number: image.routing_number,
                                      image_width: image.width,
                                      image_height: image.height,
                                      bbox_x: image.bbox_x,
                                      bbox_y: image.bbox_y,
                                      bbox_width: image.bbox_width,
                                      bbox_height: image.bbox_height,
                                      extraction_method: image.extraction_method,
                                    }, null, 2)}
                                  </pre>
                                </div>
                              </details>
                            </Col>
                            {image.ocr_text && (
                              <Col sm={12}>
                                <details>
                                  <summary className="small text-muted" style={{ cursor: 'pointer' }}>
                                    Raw OCR Text ({image.ocr_text.length} chars)
                                  </summary>
                                  <div className="small mt-1 p-2 bg-light rounded" style={{ maxHeight: '120px', overflow: 'auto' }}>
                                    <pre className="mb-0" style={{ fontSize: '0.8rem', whiteSpace: 'pre-wrap' }}>{image.ocr_text}</pre>
                                  </div>
                                </details>
                              </Col>
                            )}
                          </Row>
                        )}
                      </Card.Body>
                    </Card>
                  );
                })}
              </div>
            </Accordion.Body>
          </Accordion.Item>
        ))}
      </Accordion>
    </div>
  );
};

export default CheckImagesView;